import { useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import AskStotlesAiModal from "components/bid_module/requirement_summary_report_section/ask_question_modal/AskStotlesAiModal";
import Button from "components/ui/button/Button";
import EllipsisTooltip from "components/ui/tooltip/EllipsisTooltip";
import { useRefreshBuyerActivity } from "lib/hooks/api/buyer_activity/useRefreshBuyerActivity";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSubscription } from "lib/hooks/subscription";
import { useRestricedViewNotice } from "lib/hooks/useRestrictedRowClick";
import UIcon, { IconKey } from "lib/icons/untitled_ui/UIcon";
import { usePaywall } from "lib/providers/Paywall";
import { routes } from "lib/routes";
import { BuyerActivityObjectType, BuyerActivityStatus } from "lib/types/graphQLEnums";
import { CoreNotice } from "./types";

type Props = {
  objectId: string;
  objectType: BuyerActivityObjectType;
  summary: { status?: BuyerActivityStatus | null } | null;
  notices: CoreNotice[];
  bidName?: string | null;
  leadName?: string | null;
  buyerName?: string | null;
  buyerId?: string | null;
  noticeId?: string | null;
  noticeTitle?: string | null;
};

export default function BuyerActivityHeader({
  objectId,
  objectType,
  summary,
  notices,
  bidName,
  leadName,
  buyerName,
  buyerId,
  noticeId,
  noticeTitle,
}: Props) {
  const { mutate: refreshReport } = useRefreshBuyerActivity();
  const subscription = useSubscription();
  const { openPaywallModal } = usePaywall();
  const { data: currentUser } = useCurrentUser();
  const [isAskAiModalOpen, setIsAskAiModalOpen] = useState(false);

  // `objectId` is whichever object the report hangs off, so each link row derives its id from the
  // object type rather than taking a separate prop.
  const bidId = objectType === BuyerActivityObjectType.Bid ? objectId : undefined;
  const leadId = objectType === BuyerActivityObjectType.Lead ? objectId : undefined;
  const isStatusPending = summary?.status === BuyerActivityStatus.Pending;

  return (
    <>
      <HeaderWrapper column>
        <TitleRow>
          <Text h1>Historic buyer activity{buyerName ? `, ${buyerName}` : ""}</Text>
          <Flex gap={8} alignItems="center">
            {summary && (
              <Button
                onClick={() =>
                  refreshReport({ GenerateBuyerActivitySummaryRequest: { objectId, objectType } })
                }
                disabled={isStatusPending || notices.length === 0}
                buttonType="secondary"
                icon="refreshCcw01"
                aria-label="Refresh report"
              >
                {isStatusPending ? "Regenerating..." : "Regenerate"}
              </Button>
            )}
            {!!currentUser && objectType === BuyerActivityObjectType.Bid && (
              <Button
                icon="magicWand"
                onClick={() => {
                  if (!subscription.hasDataTypes("BIDS")) {
                    return openPaywallModal("BID_STOTLES_AI");
                  }
                  setIsAskAiModalOpen(true);
                }}
              >
                Ask Stotles AI
              </Button>
            )}
          </Flex>
        </TitleRow>
        <BuyerHeaderProperties
          bidId={bidId}
          bidName={bidName}
          leadId={leadId}
          leadName={leadName}
          buyerId={buyerId}
          buyerName={buyerName}
          noticeId={noticeId}
          noticeTitle={noticeTitle}
          loggedInUser={!!currentUser}
        />
      </HeaderWrapper>
      {objectType === BuyerActivityObjectType.Bid && (
        <AskStotlesAiModal
          resourceId={objectId}
          resourceName={bidName ?? buyerName ?? ""}
          bidId={bidId}
          noticeTitle={noticeTitle ?? undefined}
          isOpen={isAskAiModalOpen}
          onClose={() => setIsAskAiModalOpen(false)}
        />
      )}
    </>
  );
}

type PropertyRow = {
  icon: IconKey;
  label: string;
  text: string;
  onClick: () => void;
};

function BuyerHeaderProperties({
  bidId,
  bidName,
  leadId,
  leadName,
  buyerId,
  buyerName,
  noticeId,
  noticeTitle,
  loggedInUser,
}: {
  bidId?: string;
  bidName?: string | null;
  leadId?: string;
  leadName?: string | null;
  buyerId?: string | null;
  buyerName?: string | null;
  noticeId?: string | null;
  noticeTitle?: string | null;
  loggedInUser: boolean;
}) {
  const navigate = useNavigate();
  const { viewRecord } = useRestricedViewNotice("Title click");

  const properties: PropertyRow[] = [];

  if (bidId && bidName && loggedInUser) {
    properties.push({
      icon: "feather",
      label: "Bid",
      text: bidName,
      onClick: () => void navigate({ to: `${routes.bids}/${bidId}` }),
    });
  }

  if (leadId && leadName && loggedInUser) {
    properties.push({
      icon: "target04",
      label: "Lead",
      text: leadName,
      onClick: () => void navigate({ to: `${routes.leads}/${leadId}` }),
    });
  }

  if (buyerId && buyerName) {
    properties.push({
      icon: "wallet02",
      label: "Buyer",
      text: buyerName,
      onClick: () => window.open(`${routes.buyer}/${buyerId}`, "_blank", "noopener,noreferrer"),
    });
  }

  if (noticeId && noticeTitle) {
    properties.push({
      icon: "file06",
      label: "Notice",
      text: noticeTitle,
      onClick: () => {
        if (loggedInUser) {
          viewRecord(noticeId);
        } else {
          window.open(`${routes.notices}/${noticeId}`, "_blank", "noopener,noreferrer");
        }
      },
    });
  }

  if (properties.length === 0) return null;

  return (
    <PropertiesContainer>
      {properties.map(({ icon, label, text, onClick }) => (
        <Flex key={label}>
          <PropertyTitle>
            <UIcon icon={icon} color="sysTextSecondary" />
            {label}
          </PropertyTitle>
          <EllipsisTooltip fullText={text}>
            {(ref) => (
              <LinkButton ref={ref} type="button" onClick={onClick}>
                {text}
              </LinkButton>
            )}
          </EllipsisTooltip>
        </Flex>
      ))}
    </PropertiesContainer>
  );
}

const HeaderWrapper = styled(Flex)({
  padding: "0 16px",
});

const TitleRow = styled(Flex)({
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  marginBottom: 32,
});

const PropertiesContainer = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 8,
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  width: "100%",
  paddingBottom: 26,
}));

const PropertyTitle = styled.span(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 130,
  color: theme.colors.sysTextSecondary,
}));

const LinkButton = styled.button(({ theme }) => ({
  all: "unset",
  boxSizing: "border-box",
  fontWeight: 500,
  display: "-webkit-box",
  WebkitLineClamp: 1,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  maxWidth: 300,
  color: theme.colors.sysPrimaryDefault,
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
    color: theme.colors.sysPrimaryPressed,
  },
  "&:focus-visible": {
    outline: `2px solid ${theme.colors.sysPrimaryDefault}`,
    outlineOffset: 2,
    borderRadius: 2,
  },
}));
